import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Modal, Form, Input, Select, App, Typography, Space, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, UserAddOutlined, DeleteOutlined, CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { listGroups, createGroup, updateGroup, deleteGroup, assignAdminToGroup, removeAdminFromGroup, listUsers } from '../services/api';
import { confirmCritical } from '../utils/notifications';
import { useThemeStyles } from '../context/ThemeContext';
import PageHeader from '../components/PageHeader';
import { pageShellStyle } from '../theme';

const { Text } = Typography;
const PAGE_SIZE = 10;

function Groups() {
  const { message } = App.useApp();
  const { token, isSuperAdmin } = useAuth();
  const themeStyles = useThemeStyles();
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [form] = Form.useForm();
  const [assignForm] = Form.useForm();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const fetchGroups = async () => {
    if (!token || !isSuperAdmin) return;
    setLoading(true);
    try {
      const res = await listGroups(token);
      setGroups(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    if (!token) return;
    try {
      const res = await listUsers(token);
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (_) {
      setUsers([]);
    }
  };

  useEffect(() => {
    fetchGroups();
    if (isSuperAdmin) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isSuperAdmin]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const filteredGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => {
      const name = String(g.name || '').toLowerCase();
      const desc = String(g.description || '').toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [groups, searchQuery]);

  const pagedGroups = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredGroups.slice(start, start + PAGE_SIZE);
  }, [filteredGroups, currentPage]);

  useEffect(() => {
    setCurrentPage((p) => {
      const pages = Math.max(1, Math.ceil(filteredGroups.length / PAGE_SIZE));
      return p > pages ? pages : p;
    });
  }, [filteredGroups.length]);

  const openCreate = () => {
    setEditingGroup(null);
    form.setFieldsValue({ name: '', description: '' });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingGroup(record);
    form.setFieldsValue({ name: record.name, description: record.description ?? '' });
    setModalOpen(true);
  };

  const onFinish = async (values) => {
    setActionLoading(true);
    try {
      if (editingGroup) {
        await updateGroup(token, editingGroup._id, values.name, values.description);
        message.success('Group updated');
      } else {
        await createGroup(token, values.name, values.description);
        message.success('Group created');
      }
      setModalOpen(false);
      fetchGroups();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to save group');
    } finally {
      setActionLoading(false);
    }
  };

  const onDelete = async (groupId) => {
    try {
      await deleteGroup(token, groupId);
      message.success('Group deleted');
      fetchGroups();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to delete group');
    }
  };

  const openAssign = (groupId) => {
    setSelectedGroupId(groupId);
    assignForm.resetFields();
    setAssignModalOpen(true);
  };

  const onAssignSubmit = async (values) => {
    if (!selectedGroupId || !values.user_id) return;
    try {
      await assignAdminToGroup(token, selectedGroupId, values.user_id);
      message.success('Admin assigned');
      setAssignModalOpen(false);
      fetchGroups();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to assign admin');
    }
  };

  const handleRemoveAdmin = async (groupId, adminUserId) => {
    try {
      await removeAdminFromGroup(token, groupId, adminUserId);
      message.success('Admin removed');
      fetchGroups();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to remove admin');
    }
  };

  const headerCellProps = () => ({
    style: {
      background: themeStyles.userTableHeaderBg,
      fontWeight: 600,
      borderBottom: `1px solid ${themeStyles.userTableHeaderBorder}`,
      color: themeStyles.textPrimary,
      textAlign: 'left',
    },
  });

  if (!isSuperAdmin) {
    return (
      <div style={{ ...pageShellStyle, background: themeStyles.contentBg }}>
        <Text style={{ color: themeStyles.textSecondary }}>Super admin access required.</Text>
      </div>
    );
  }

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      onHeaderCell: headerCellProps,
      render: (t) => (
        <Text strong style={{ color: themeStyles.textPrimary, fontSize: 13 }}>
          {t}
        </Text>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      onHeaderCell: headerCellProps,
      ellipsis: true,
      render: (t) => (
        <Text style={{ color: themeStyles.textSecondary, fontSize: 13 }}>
          {t || '—'}
        </Text>
      ),
    },
    {
      title: 'Administrator',
      dataIndex: 'admin_ids',
      key: 'admin_ids',
      onHeaderCell: headerCellProps,
      render: (ids = [], record) => {
        const hasAdmin = (record.admin_ids?.length || 0) >= 1;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
            {(ids || []).map((uid) => (
              <div
                key={uid}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  minHeight: 32,
                }}
              >
                <Text type="secondary" style={{ fontSize: 13, color: themeStyles.textSecondary }}>
                  {users.find((u) => u._id === uid)?.username ?? uid}
                </Text>
                <Tooltip title="Remove">
                  <Button
                    type="text"
                    size="small"
                    danger
                    aria-label="Remove"
                    icon={<CloseOutlined />}
                    className="groups-table-icon-btn"
                    onClick={() => handleRemoveAdmin(record._id, uid)}
                  />
                </Tooltip>
              </div>
            ))}
            {!hasAdmin && (
              <Button
                type="text"
                size="small"
                icon={<UserAddOutlined />}
                className="groups-table-icon-btn"
                onClick={() => openAssign(record._id)}
                style={{ color: themeStyles.textSecondary }}
              >
                Add admin
              </Button>
            )}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      onHeaderCell: headerCellProps,
      render: (_, record) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 12,
            minHeight: 32,
          }}
        >
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              aria-label="Edit"
              icon={<EditOutlined />}
              className="groups-table-icon-btn"
              style={{ color: themeStyles.textSecondary }}
              onClick={() => openEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              size="small"
              danger
              aria-label="Delete"
              icon={<DeleteOutlined />}
              className="groups-table-icon-btn"
              onClick={() => confirmCritical({
                title: 'Delete this group?',
                content: 'This will delete the group and all its projects. All associated data will be permanently removed.',
                okText: 'Delete group',
                onOk: () => onDelete(record._id),
              })}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div style={{ ...pageShellStyle, background: themeStyles.contentBg }}>
      <PageHeader
        title="Groups"
        actions={(
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Create group
          </Button>
        )}
      />

      <div style={{ marginBottom: 16, width: '100%', maxWidth: 560 }}>
        <Input
          allowClear
          placeholder="Search by name or description"
          prefix={<SearchOutlined style={{ color: themeStyles.textTertiary }} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ width: '100%' }}
        />
      </div>

      <div
        className="groups-table-surface"
        style={{
          borderRadius: 0,
          border: `1px solid ${themeStyles.shellDivider}`,
        }}
      >
        <Table
          className="groups-table"
          dataSource={pagedGroups}
          columns={columns}
          rowKey="_id"
          loading={loading}
          size="small"
          tableLayout="fixed"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize: PAGE_SIZE,
            total: filteredGroups.length,
            showSizeChanger: false,
            size: 'small',
            className: 'groups-table-pagination',
            hideOnSinglePage: false,
            onChange: (page) => setCurrentPage(page),
          }}
          onRow={(record, rowIndex) => {
            const globalIndex = (currentPage - 1) * PAGE_SIZE + rowIndex;
            const baseBg = globalIndex % 2 === 0
              ? themeStyles.userTableZebraA
              : themeStyles.userTableZebraB;
            return {
              style: { background: baseBg },
              onMouseEnter: (e) => {
                e.currentTarget.style.background = themeStyles.userTableRowHoverBg;
              },
              onMouseLeave: (e) => {
                e.currentTarget.style.background = baseBg;
              },
            };
          }}
        />
      </div>

      <Modal title={editingGroup ? 'Edit group' : 'Create group'} open={modalOpen} onCancel={() => setModalOpen(false)} footer={null}>
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={actionLoading}>{actionLoading ? 'Saving...' : 'Save'}</Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setModalOpen(false)} disabled={actionLoading}>Cancel</Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Add admin to group" open={assignModalOpen} onCancel={() => setAssignModalOpen(false)} footer={null}>
        <Form form={assignForm} layout="vertical" onFinish={onAssignSubmit}>
          <Form.Item name="user_id" label="User" rules={[{ required: true }]}>
            {(() => {
              const assignedToAnyGroup = new Set();
              groups.forEach((g) => (g.admin_ids || []).forEach((id) => assignedToAnyGroup.add(id)));
              const options = users
                .filter((u) => u.role === 'admin' && !assignedToAnyGroup.has(u._id))
                .map((u) => ({ label: u.username, value: u._id }));
              return <Select placeholder="Select admin to add (only unassigned)" options={options} />;
            })()}
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">Add</Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setAssignModalOpen(false)}>Cancel</Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Groups;
